// XHR-style events keep the file and recording workflows on the same transport.
// Only the initial small request needs a current account token. Chunk credentials
// are scoped to one file and survive token refreshes and page reloads for 7 days.
import { isPersistentUploadFailure, uploadErrorCode } from './uploadRecovery';
import { withClientVersion } from './clientVersion';
import { trackEvent } from './analytics';
const STORAGE_PREFIX = "instanttranscriber.upload.v1:";
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
const MIN_CHUNK_BYTES = 128 * 1024;
const activeUploads = new Set();
const duplicateUpload = () => Object.assign(new Error('This file is already uploading. Return to the active upload, or select it again after that upload stops.'), {
  code: 'upload_already_active', status: 409,
  payload: { code: 'upload_already_active', error: 'This file is already uploading. Return to the active upload, or select it again after that upload stops.' },
});
export function initialChunkBytes() {
  const nav = typeof navigator==='undefined' ? null : navigator;
  const connection = nav?.connection;
  if (connection?.saveData || ['slow-2g','2g','3g'].includes(connection?.effectiveType)) return MIN_CHUNK_BYTES;
  if (nav?.userAgentData?.mobile || (typeof window!=='undefined' && window.matchMedia?.('(pointer: coarse)')?.matches)) return 512 * 1024;
  return 4 * 1024 * 1024;
}

class UploadRequestError extends Error {
  constructor(response) {
    super(typeof response.data?.error === "string" ? response.data.error
      : response.data?.error?.message || `Upload error (${response.status})`);
    this.status = response.status;
    this.payload = response.data;
    this.code = uploadErrorCode(response.data, response.status);
    this.retryAfterMs = response.retryAfterMs;
    this.failureOrigin = response.failureOrigin || 'proxy_or_transport';
  }
}

export async function uploadFingerprint(file, userId) {
  const sample = new Blob([file.slice(0, 65536), file.slice(Math.max(0, file.size - 65536))]);
  const digest = await crypto.subtle.digest("SHA-256", await sample.arrayBuffer());
  const hash = Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, "0")).join("");
  return STORAGE_PREFIX + JSON.stringify([userId, file.name, file.size, file.lastModified, hash]);
}

export async function uniqueUploadFiles(files) {
  const seen = new Set(), unique = [];
  for (const file of files) {
    const key = await uploadFingerprint(file, '');
    if (!seen.has(key)) { seen.add(key); unique.push(file); }
  }
  return unique;
}

export class ResumableUpload {
  constructor({ baseUrl, file, userId, getToken, storage, wait = delay, xhrFactory = () => new XMLHttpRequest(),
    chunkBytes = initialChunkBytes(), idleTimeoutMs = 45000, recordingDiagnostics }) {
    Object.assign(this, { baseUrl, file, userId, getToken, wait, xhrFactory });
    try { this.storage = storage === undefined ? window.localStorage : storage; } catch { this.storage = null; }
    this.upload = {};
    this.status = 0;
    this.responseText = "";
    this.aborted = false;
    this.retryCount = 0;
    this.resumedRetryCount = 0;
    this.resumedFromStorage = false;
    this.lastRetryStage = '';
    this.lastRetryCode = '';
    this.lastRetryOrigin = '';
    this.chunkBytes = chunkBytes;
    this.idleTimeoutMs = idleTimeoutMs;
    this.fastChunks = 0;
    this.startedAt = Date.now();
    this.lastProgressBytes = 0;
    this.operation = 'create';
    this.recordingDiagnostics = recordingDiagnostics;
  }

  recoveryAnalytics() {
    return { retry_count: Math.min(this.retryCount, 1000), upload_recovered: this.retryCount > 0,
      upload_resumed: this.resumedFromStorage };
  }

  transferContext(cancelReason) {
    return { retry_count: Math.min(this.retryCount+this.resumedRetryCount,1000),
      ...(this.lastRetryCode ? {last_retry_code:this.lastRetryCode,last_retry_stage:this.lastRetryStage,
        last_retry_origin:this.lastRetryOrigin} : {}),
      ...(Number.isFinite(this.lastChunkMs) ? {last_chunk_ms:this.lastChunkMs,last_chunk_bytes:this.lastChunkBytes} : {}),
      transfer_ms: Math.min(86400000,Math.max(0,(this.transferFinishedAt ?? Date.now())-this.startedAt)),
      ...(cancelReason ? {cancel_reason:cancelReason} : {}),
      ...(this.recordingDiagnostics ? { recording_diagnostics: this.recordingDiagnostics } : {}),
      ...(this.error?.code && !this.error.canResume ? { terminal_error_code: this.error.code } : {}),
    };
  }

  transferAnalytics() {
    return { elapsed_ms: this.transferContext().transfer_ms, retry_count:this.retryCount,
      event_source: `${this.operation}_${this.lastRetryOrigin || 'no_retry'}${this.lastRetryCode ? '_'+this.lastRetryCode : ''}`,
      upload_progress_percent: Math.round(100*this.lastProgressBytes/this.file.size),
      ...(this.lastRetryCode ? {last_retry_code:this.lastRetryCode} : {}),
      ...(Number.isFinite(this.lastChunkMs) ? {chunk_ms:this.lastChunkMs,chunk_bytes:this.lastChunkBytes} : {}),
    };
  }

  _connection(state, retryAt) {
    if (this.aborted || (this.connectionState===state && this.connectionRetryCount===this.retryCount
        && this.connectionRetryAt===retryAt)) return;
    this.connectionState=state;this.connectionRetryCount=this.retryCount;this.connectionRetryAt=retryAt;
    this.onconnection?.({ state, retryAt, retryCount:this.retryCount });
  }

  _transient(error, stage) {
    return !isPersistentUploadFailure(error) && (!error.status
      || [408,409,429].includes(error.status) || error.status>=500
      || (stage==='chunk' && error.status===400 && error.code==='http_400' && error.failureOrigin==='proxy_or_transport'));
  }

  _save() {
    try { this.storage?.setItem(this.key, JSON.stringify({id:this.session.id,upload_token:this.session.upload_token,
      expires_at:this.session.expires_at,retry_count:Math.min(this.retryCount+this.resumedRetryCount,1000),
      last_retry_code:this.lastRetryCode,last_retry_stage:this.lastRetryStage,last_retry_origin:this.lastRetryOrigin })); }
    catch { /* In-tab resume still works. */ }
  }

  _forget() {
    // A rejected competing upload must never remove the active upload's resume
    // credential, even if both refer to the same file.
    try {
      const stored = JSON.parse(this.storage?.getItem(this.key) || 'null');
      if (this.key && this.session && stored?.id === this.session.id) this.storage?.removeItem(this.key);
    } catch {}
  }

  async _waitForRetry(milliseconds, wakeOnConnection = true) {
    if (this.wait !== delay || typeof window === 'undefined') return this.wait(milliseconds);
    await new Promise(resolve => {
      const finish = () => {
        clearTimeout(timer); window.removeEventListener('online', wake);
        document.removeEventListener('visibilitychange', wake);
        if (this.retryWake === finish) this.retryWake = null;
        resolve();
      };
      const wake = () => { if (wakeOnConnection && !document.hidden && navigator.onLine !== false) finish(); };
      const timer = setTimeout(finish, milliseconds);
      window.addEventListener('online', wake); document.addEventListener('visibilitychange', wake);
      this.retryWake = finish;
      if (this.aborted) finish();
    });
  }

  async _waitUntilOnline() {
    if (typeof navigator === 'undefined' || navigator.onLine !== false || typeof window === 'undefined') return;
    this._connection('offline');
    while (!this.aborted && navigator.onLine === false) await this._waitForRetry(30000);
    if (!this.aborted) this._connection('reconnecting');
  }

  _progress(loaded) {
    this.lastProgressBytes = Math.min(this.file.size,Math.max(0,loaded || 0));
    this.upload.onprogress?.({ lengthComputable: true, loaded, total: this.file.size });
  }

  _preparation(state) {
    if (this.aborted || state.result) return;
    const stages = ["preparing", "waiting", "checking", "extracting", "converting", "saving"];
    const stage = stages.includes(state.preparation_stage) ? state.preparation_stage : "preparing";
    this.preparationStartedAt = this.preparationStartedAt || Date.now();
    if (stage === 'waiting' && !this.preparationWaitingAt) this.preparationWaitingAt = Date.now();
    if (stage !== 'waiting' && this.preparationWaitingAt && !this.preparationWaitMeasured) {
      this.preparationWaitMeasured = true;
      trackEvent('upload_preparation_timing', { operation: 'upload_preparation', event_source: 'waiting',
        elapsed_ms: Math.min(86400000, Date.now() - this.preparationWaitingAt) });
    }
    this.onpreparation?.({ stage, startedAt: this.preparationStartedAt,
      continuesInBackground: state.preparation_continues_in_background === true });
  }

  _request(method, path, authorization, body, progress, { background = false } = {}) {
    if (this.aborted) return Promise.reject(new Error("Upload cancelled"));
    return new Promise((resolve, reject) => {
      const xhr = this.xhrFactory();
      if (background) this.monitorXhr = xhr;
      else this.currentXhr = xhr;
      xhr.open(method, withClientVersion(this.baseUrl + path));
      xhr.setRequestHeader("Authorization", authorization);
      if (typeof body === "string") xhr.setRequestHeader("Content-Type", "application/json");
      else if (body instanceof Blob) xhr.setRequestHeader("Content-Type", "application/octet-stream");
      // A slow connection may take many minutes per chunk. Only a lack of
      // progress times out a chunk; the transfer has no fixed request deadline.
      xhr.timeout = background ? 10000 : method === "PATCH" || path === "/upload" ? 0
        : path.endsWith("/complete") ? 120000 : 180000;
      let idleTimer, idleExpired = false;
      const requestStarted = Date.now();
      const recordChunk = () => {
        if (method==='PATCH') {
          this.lastChunkMs = Math.min(86400000,Math.max(0,Date.now()-requestStarted));
          this.lastChunkBytes = body?.size || 0;
        }
      };
      const watchProgress = () => {
        clearTimeout(idleTimer);
        if (method === "PATCH") idleTimer = setTimeout(() => { idleExpired = true; xhr.abort(); }, this.idleTimeoutMs);
      };
      const connectionError = (cause = 'upload_connection_interrupted') => {
        clearTimeout(idleTimer);
        recordChunk();
        reject(Object.assign(new Error(this.aborted ? "Upload cancelled"
          : "Connection interrupted. Select the same file to resume the upload."),
        { code: this.aborted ? 'upload_cancelled' : idleExpired ? 'upload_stalled' : cause,
          failureOrigin:'network' }));
      };
      xhr.upload.onprogress = event => { watchProgress();
        if (event.loaded>0 && method==='PATCH') this._connection('uploading');
        progress?.(event);
      };
      xhr.onload = () => {
        clearTimeout(idleTimer);
        recordChunk();
        let data = {};
        try { data = JSON.parse(xhr.responseText || "{}"); } catch {}
        const retryAfter = xhr.getResponseHeader?.('Retry-After');
        const seconds = Number(retryAfter);
        const retryAfterMs = retryAfter ? (Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now()) : 0;
        const appCode = data?.code && uploadErrorCode(data,xhr.status)===data.code;
        resolve({ status: xhr.status, data, retryAfterMs: Math.max(0, retryAfterMs || 0),
          durationMs:Date.now()-requestStarted,
          failureOrigin:xhr.getResponseHeader?.('X-IT-Upload-Handler')==='application' || appCode ? 'application' : 'proxy_or_transport' });
      };
      xhr.onerror = xhr.onabort = () => connectionError();
      xhr.ontimeout = () => connectionError('upload_timeout');
      watchProgress();
      xhr.send(body ?? null);
    });
  }

  async _retry(operation, stage = 'upload_request') {
    this.operation = stage;
    let recoveryStarted;
    for (let attempt = 0; ; attempt += 1) {
      await this._waitUntilOnline();
      if (this.aborted) throw new Error("Upload cancelled");
      try {
        if (attempt) this._connection('reconnecting');
        const result = await operation();
        if (result.status >= 400) throw new UploadRequestError(result);
        if (attempt) this._connection('uploading');
        return result;
      } catch (error) {
        error.stage = stage;
        error.retry_count = this.retryCount;
        const transient = this._transient(error,stage);
        if (this.aborted || !transient || attempt >= (stage==='chunk' ? 5 : 9)
            || (recoveryStarted!==undefined && Date.now()-recoveryStarted>=120000) || error.retryAfterMs>30000) throw error;
        recoveryStarted ??= Date.now();
        this.retryCount += 1;
        this.lastRetryStage = stage;
        this.lastRetryCode = uploadErrorCode(error.payload || { code: error.code }, error.status);
        this.lastRetryOrigin = error.failureOrigin || 'network';
        if (this.session) this._save();
        if (stage==='chunk') { this.chunkBytes=Math.max(MIN_CHUNK_BYTES,Math.floor(this.chunkBytes/2)); this.fastChunks=0; }
        const pause = Math.max(Math.min(1000*(2**attempt),10000),error.retryAfterMs || 0);
        this._connection('retrying',Date.now()+pause);
        trackEvent('upload_retry',{operation:stage,error_code:this.lastRetryCode,retry_count:this.retryCount,
          event_source:`${stage}_${this.lastRetryOrigin}`,elapsed_ms:Date.now()-this.startedAt,
          chunk_bytes:this.lastChunkBytes,chunk_ms:this.lastChunkMs});
        await this._waitForRetry(pause, !error.retryAfterMs);
      }
    }
  }

  async _legacy() {
    // Rolling deployment compatibility: an older backend may still serve /upload.
    // Authentication failures never fall back to sending the file.
    this._forget();
    this.session = null;
    const form = new FormData();
    form.append("file", this.file);
    const response = await this._request("POST", "/upload", `Bearer ${await this.getToken()}`, form,
      event => {
        if (event.lengthComputable) {
          this._progress(event.loaded * this.file.size / event.total);
          if (event.loaded === event.total) { this.transferFinishedAt=Date.now(); this._preparation({}); }
        }
      });
    return { result: response.data, result_status: response.status };
  }

  async _complete(path, auth) {
    // The completion request prepares the file; a separate read-only request
    // reports the actual stage while it runs. Poll failures never retry a job.
    let stopped = false, timer;
    const poll = async () => {
      try {
        const response = await this._request("GET", path, auth, undefined, undefined, { background: true });
        if (!stopped && !this.aborted && response.status === 200) this._preparation(response.data);
      } catch { /* Completion remains authoritative during temporary outages. */ }
      if (!stopped && !this.aborted) timer = setTimeout(poll, 1500);
    };
    timer = setTimeout(poll, 1000);
    try {
      return await this._retry(() => this._request("POST", `${path}/complete`, auth, "{}"), 'preparation');
    } finally {
      stopped = true;
      clearTimeout(timer);
      this.monitorXhr?.abort();
      this.monitorXhr = null;
    }
  }

  async _run() {
    this.key = await uploadFingerprint(this.file, this.userId);
    if (this.aborted) throw new Error("Upload cancelled");
    if (activeUploads.has(this.key)) throw duplicateUpload();
    activeUploads.add(this.key);
    try {
      const locks = typeof navigator === 'undefined' ? null : navigator.locks;
      if (locks?.request) {
        // Lock names are opaque and never enter analytics. ifAvailable prevents
        // a queued duplicate from starting again after the first has finished.
        const hash = await crypto.subtle.digest('SHA-256', await new Blob([this.key]).arrayBuffer());
        const lockName = 'instanttranscriber-upload:' + Array.from(new Uint8Array(hash), n => n.toString(16).padStart(2,'0')).join('');
        let entered = false;
        try {
          return await locks.request(lockName, { ifAvailable: true }, lock => {
            entered = true;
            if (!lock) throw duplicateUpload();
            return this._transfer();
          });
        } catch (error) {
          if (entered || !['SecurityError','NotSupportedError'].includes(error?.name)) throw error;
          // Private/embedded environments may expose a disabled lock manager.
        }
      }
      return await this._transfer();
    } finally { activeUploads.delete(this.key); }
  }

  async _transfer() {
    if (this.aborted) throw new Error('Upload cancelled');
    try {
      const saved = JSON.parse(this.storage?.getItem(this.key) || "null");
      if (saved?.expires_at > Date.now() / 1000 && /^[a-f0-9-]{36}$/.test(saved.id)
          && /^[A-Za-z0-9_-]{43,100}$/.test(saved.upload_token)) {
        this.session = saved;
        this.resumedRetryCount = Number.isInteger(saved.retry_count) && saved.retry_count>=0 && saved.retry_count<=1000 ? saved.retry_count : 0;
        if (this.resumedRetryCount && typeof saved.last_retry_code === 'string'
            && (uploadErrorCode({code:saved.last_retry_code}) === saved.last_retry_code || /^http_[45][0-9]{2}$/.test(saved.last_retry_code))) {
          this.lastRetryCode = saved.last_retry_code;
          this.lastRetryStage = ['create','resume','chunk','preparation'].includes(saved.last_retry_stage) ? saved.last_retry_stage : '';
          this.lastRetryOrigin = ['application','proxy_or_transport','network'].includes(saved.last_retry_origin) ? saved.last_retry_origin : '';
        }
      }
    } catch {}

    let state;
    if (this.session) {
      const response = await this._retry(async () => {
        const res = await this._request("GET", `/uploads/${this.session.id}`, `Upload ${this.session.upload_token}`);
        // Creation may have failed before reaching the server, or expired sessions
        // may have been removed. Creating with the same credential is idempotent.
        if ([403, 404, 410].includes(res.status)) return { status: 200, data: null };
        return res;
      }, 'resume');
      state = response.data;
      if (state) {
        this.sessionCreated = true;
        this.resumedFromStorage = state.offset > 0 || Boolean(state.result) || state.state === 'preparing';
      }
      if (!state) { this._forget(); this.session = null; this.resumedRetryCount=0; }
    }
    if (!this.session) {
      const bytes = crypto.getRandomValues(new Uint8Array(32));
      const token = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      this.session = { id: crypto.randomUUID(), upload_token: token, expires_at: Date.now() / 1000 + 7 * 86400 };
      this._save();
      const response = await this._retry(async () => {
        const res = await this._request("POST", "/uploads", `Bearer ${await this.getToken()}`, JSON.stringify({
          filename: this.file.name, size: this.file.size, upload_id: this.session.id, upload_token: this.session.upload_token,
        }));
        if ([404, 405].includes(res.status)) return { status: 200, legacy: true };
        return res;
      }, 'create');
      if (response.legacy) return this._legacy();
      state = response.data;
      this.sessionCreated = true;
      this.session = { id: state.id, upload_token: state.upload_token, expires_at: state.expires_at };
      this._save();
    }
    if (this.recordingDiagnostics) this._reportContext();

    const path = `/uploads/${this.session.id}`;
    const auth = `Upload ${this.session.upload_token}`;
    this._progress(state.offset);
    while (state.offset < this.file.size) {
      const response = await this._retry(async () => {
        // Always reconcile after a lost acknowledgement; do not resend bytes that
        // the server already persisted. The server also rejects concurrent offsets.
        const saved = await this._request("GET", path, auth);
        if (saved.status >= 400) return saved;
        const savedState = saved.data;
        this._progress(savedState.offset);
        if (savedState.offset >= this.file.size) return saved;
        const offset = savedState.offset;
        const chunk = this.file.slice(offset, Math.min(this.file.size, offset + Math.min(savedState.chunk_size,this.chunkBytes)));
        const result = await this._request("PATCH", `${path}?offset=${offset}`, auth, chunk,
          event => { if (event.lengthComputable) this._progress(offset + event.loaded); });
        if (result.status<400) {
          if (result.durationMs>20000) { this.chunkBytes=Math.max(MIN_CHUNK_BYTES,Math.floor(this.chunkBytes/2)); this.fastChunks=0; }
          else if (result.durationMs<4000 && ++this.fastChunks>=3) {
            this.chunkBytes=Math.min(savedState.chunk_size,4*1024*1024,this.chunkBytes*2); this.fastChunks=0;
          }
        }
        return result;
      }, 'chunk');
      state = response.data;
      this._progress(state.offset);
    }

    this.transferFinishedAt=Date.now();
    if (!state.result) this._preparation(state);
    while (!state.result) {
      const response = await this._complete(path, auth);
      state = response.data;
      if (!state.result) {
        this._preparation(state);
        await this.wait(2000);
      }
      if (this.aborted) throw new Error("Upload cancelled");
    }
    if (this.preparationStartedAt && !this.aborted) {
      trackEvent('upload_preparation_timing', { operation: 'upload_preparation', event_source: 'total',
        elapsed_ms: Math.min(86400000, Date.now() - this.preparationStartedAt) });
    }
    this._forget();
    return state;
  }

  send() {
    this.done = this._run().then(state => {
      if (this.aborted) return;
      this.status = state.result_status || 200;
      this._reportContext();
      this.responseText = JSON.stringify(state.result);
      return this.onload?.();
    }).catch(error => {
      if (this.aborted) return;
      this.error = error;
      error.canResume = this._transient(error,error.stage);
      this._reportContext();
      if (error.status) {
        if (!error.canResume) {
          this._cancelSession();
          this._forget();
        }
        this.status = error.status;
        this.responseText = JSON.stringify({ ...error.payload, error: error.message,
          error_code: error.code, operation: error.stage, retry_count: error.retry_count || 0,
          resume_available:error.canResume });
        return this.onload?.();
      }
      return this.onerror?.(error);
    });
  }

  abort() {
    if (this.aborted) return;
    this.aborted = true;
    this.currentXhr?.abort();
    this.monitorXhr?.abort();
    this.retryWake?.();
    this._forget();
    this._cancelSession('user');
    this.onabort?.();
  }

  _reportContext() {
    if (!this.session || !this.sessionCreated) return;
    fetch(withClientVersion(`${this.baseUrl}/uploads/${this.session.id}/diagnostics`), {
      method:'POST',headers:{Authorization:`Upload ${this.session.upload_token}`,'Content-Type':'application/json'},
      body:JSON.stringify(this.transferContext()),keepalive:true,
    }).catch(() => {});
  }

  _cancelSession(reason='terminal_failure') {
    if (this.session && this.sessionCreated) {
      // Cancellation is best-effort; unclaimed sessions are cleaned after expiry.
      fetch(withClientVersion(`${this.baseUrl}/uploads/${this.session.id}`), {
        method: "DELETE", headers: { Authorization: `Upload ${this.session.upload_token}`, 'Content-Type':'application/json' },
        body:JSON.stringify(this.transferContext(reason)), keepalive: true,
      }).catch(() => {});
    }
  }
}
